import { useContext, useEffect, useState } from 'react';
import MyInfoContext from 'contexts/MyInfoContext';
import { ExaOneAccessSnapshot, getCachedExaOneAccess, loadExaOneAccess, readAuthUserId, subscribeExaOneAccess } from 'services/exaOneAccessCache';

const useExaOneAccess = (enabled = true): boolean => {
  const { isLogin, userId } = useContext(MyInfoContext);
  const [snapshot, setSnapshot] = useState<ExaOneAccessSnapshot | undefined>(() =>
    enabled && isLogin ? getCachedExaOneAccess(userId) : undefined
  );

  useEffect(() => {
    if (!enabled || !isLogin || !userId) {
      setSnapshot(undefined);
      return undefined;
    }
    let disposed = false;
    let displayed = getCachedExaOneAccess(userId);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = (next?: ExaOneAccessSnapshot) => {
      if (timer !== undefined) clearTimeout(timer);
      if (next && next.expiresAt > Date.now()) timer = setTimeout(refresh, next.expiresAt - Date.now() + 1);
    };
    const update = () => {
      if (disposed) return;
      const next = getCachedExaOneAccess(userId, displayed !== undefined);
      displayed = next;
      setSnapshot(next);
      schedule(next);
    };
    async function refresh() {
      if (disposed || document.visibilityState === 'hidden') return;
      await loadExaOneAccess(userId);
      // 권한 거부나 계정 전환 중 끝난 이전 요청의 반환값은 사용하지 않는다.
      update();
    }
    const onAuthChanged = () => { update(); void refresh(); };
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === 'llAccessToken' || event.key === 'llRefreshToken') onAuthChanged();
    };
    window.addEventListener('storage', onStorage);
    const unsubscribe = subscribeExaOneAccess(update);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refresh);
    update();
    void refresh();
    return () => {
      disposed = true;
      if (timer !== undefined) clearTimeout(timer);
      window.removeEventListener('storage', onStorage);
      unsubscribe();
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [enabled, isLogin, userId]);

  return enabled && isLogin && userId === readAuthUserId()
    && snapshot !== undefined && snapshot === getCachedExaOneAccess(userId, true) && snapshot.available;
};

export default useExaOneAccess;
